import React, { useState, useEffect } from 'react';
import { usersApi, loanProductsApi, savingsProductsApi } from '../lib/api';
import { formatDate, getStatusColor, capitalizeFirst, formatCurrency, formatPercentage } from '../lib/utils';
import {
  Settings, Users, ShieldCheck, Package, Plus, Edit, Trash2, MoreHorizontal
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';

const SettingsPage = () => {
  return (
    <div className="space-y-6 fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-2">
          SYSTEM CONFIGURATION
        </p>
        <h1 className="font-display text-3xl font-bold text-[#1f1f1f]">
          Settings
        </h1>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="border-b border-gray-200 w-full justify-start bg-transparent p-0">
          <TabsTrigger
            value="users"
            className="px-6 py-3 text-sm font-medium text-gray-500 data-[state=active]:text-[#1f1f1f] data-[state=active]:border-b-2 data-[state=active]:border-[#C5A059] rounded-none"
          >
            <Users className="w-4 h-4 mr-2" />
            User Management
          </TabsTrigger>
          <TabsTrigger
            value="loan-products"
            className="px-6 py-3 text-sm font-medium text-gray-500 data-[state=active]:text-[#1f1f1f] data-[state=active]:border-b-2 data-[state=active]:border-[#C5A059] rounded-none"
          >
            <Package className="w-4 h-4 mr-2" />
            Loan Products
          </TabsTrigger>
          <TabsTrigger
            value="savings-products"
            className="px-6 py-3 text-sm font-medium text-gray-500 data-[state=active]:text-[#1f1f1f] data-[state=active]:border-b-2 data-[state=active]:border-[#C5A059] rounded-none"
          >
            <Package className="w-4 h-4 mr-2" />
            Savings Products
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <UsersTab />
        </TabsContent>

        <TabsContent value="loan-products">
          <LoanProductsTab />
        </TabsContent>

        <TabsContent value="savings-products">
          <SavingsProductsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await usersApi.list();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersApi.delete(userId);
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const roleColors = {
    admin: 'bg-red-100 text-red-800',
    manager: 'bg-purple-100 text-purple-800',
    loan_officer: 'bg-blue-100 text-blue-800',
    accountant: 'bg-emerald-100 text-emerald-800',
    teller: 'bg-gray-100 text-gray-800',
    auditor: 'bg-amber-100 text-amber-800'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#1f1f1f]">Users</h3>
          <p className="text-sm text-gray-500">Manage system users and their roles</p>
        </div>
        <Button
          onClick={() => setShowNewModal(true)}
          className="bg-[#1f1f1f] hover:bg-[#333333]"
          data-testid="new-user-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      <div className="bg-white border border-gray-200 overflow-hidden">
        <table className="w-full" data-testid="users-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Role
              </th>
              <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-0.5 text-xs font-medium ${roleColors[user.role] || 'bg-gray-100 text-gray-800'}`}>
                      {capitalizeFirst(user.role.replace(/_/g, ' '))}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-2.5 py-0.5 text-xs font-medium ${user.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => deleteUser(user.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <NewUserModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSuccess={() => {
          setShowNewModal(false);
          loadUsers();
        }}
      />
    </div>
  );
};

const NewUserModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'teller'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await usersApi.create(formData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Add New User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              data-testid="user-name-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
              data-testid="user-email-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Password *</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              required
              data-testid="user-password-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Role *</Label>
            <Select value={formData.role} onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}>
              <SelectTrigger data-testid="user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="loan_officer">Loan Officer</SelectItem>
                <SelectItem value="accountant">Accountant</SelectItem>
                <SelectItem value="teller">Teller</SelectItem>
                <SelectItem value="auditor">Auditor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="submit-new-user">
              {loading ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const LoanProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await loanProductsApi.list();
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#1f1f1f]">Loan Products</h3>
          <p className="text-sm text-gray-500">Configure loan products and their terms</p>
        </div>
        <Button className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="new-loan-product-button">
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin" />
          </div>
        ) : (
          products.map((product) => (
            <div key={product.id} className="p-6 border border-gray-200 hover:border-[#C5A059] transition-colors">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-[#1f1f1f]">{product.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{product.code}</p>
                </div>
                <span className={`px-2.5 py-0.5 text-xs font-medium ${product.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'}`}>
                  {product.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                <div>
                  <p className="text-gray-500">Amount Range</p>
                  <p className="font-medium">{formatCurrency(product.min_amount)} - {formatCurrency(product.max_amount)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Interest Rate</p>
                  <p className="font-medium text-[#C5A059]">{formatPercentage(product.interest_rate)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Term Range</p>
                  <p className="font-medium">{product.min_term_months} - {product.max_term_months} months</p>
                </div>
                <div>
                  <p className="text-gray-500">Type</p>
                  <p className="font-medium">{product.is_group_loan ? 'Group' : 'Individual'}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const SavingsProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await savingsProductsApi.list();
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#1f1f1f]">Savings Products</h3>
          <p className="text-sm text-gray-500">Configure deposit products and interest rates</p>
        </div>
        <Button className="bg-[#1f1f1f] hover:bg-[#333333]" data-testid="new-savings-product-button">
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1f1f1f] rounded-full animate-spin" />
          </div>
        ) : (
          products.map((product) => (
            <div key={product.id} className="p-6 border border-gray-200 hover:border-[#C5A059] transition-colors">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-[#1f1f1f]">{product.name}</h4>
                  <p className="text-sm text-gray-500 mt-1">{product.code} • {capitalizeFirst(product.account_type)}</p>
                </div>
                <span className={`px-2.5 py-0.5 text-xs font-medium ${product.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-800'}`}>
                  {product.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                <div>
                  <p className="text-gray-500">Min Opening Balance</p>
                  <p className="font-medium">{formatCurrency(product.min_opening_balance)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Interest Rate</p>
                  <p className="font-medium text-[#C5A059]">{formatPercentage(product.interest_rate)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Compounding</p>
                  <p className="font-medium">{capitalizeFirst(product.compounding_period)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Lock-in Period</p>
                  <p className="font-medium">{product.lock_in_period_days || 0} days</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
